import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { CONSTANTS } from '../../../../Constants/Constants';
import { CollaborationOperationsService } from '../../../../services/common/collaboration-operations.service';
import { CollaborationApiService } from '../../../../services/api/collaboration-api.service';
import { CollabDialogDataService } from '../../../../services/common/collab-dialog-data.service';
import { PublishRequest } from '../../../../models/dtos/Classes/PublishRequest';
import { CollaborationState } from '../../../../models/dtos';
import { IPublishCollabDataFormGroup } from '../../../../models/collaboration/IPublishCollabDataFormGroup';
import { IPublishCollabDialogData } from '../../../../models/collaboration/IPublishCollabDialogData';
import { IPublishCollabResult } from '../../../../models/collaboration/IPublishCollabResult';
import { CommonDataService } from '../../../../services/common-data.service';
import { GocCollaborationRestrictionHandlerService } from '../../../../services/common/goc-collaboration-restriction-handler.service';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-publish-collab-data-dialog',
  imports: [   
    TranslatePipe,
    MatButtonModule,
    MatLabel,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatDialogModule,
  ],
  templateUrl: './publish-collab-data-dialog.component.html',
  styleUrl: './publish-collab-data-dialog.component.scss'
})
export class PublishCollabDataDialogComponent implements OnInit {

  publishCollabFormGroup!: FormGroup<IPublishCollabDataFormGroup>;

  constructor(
    public dialogRef: MatDialogRef<PublishCollabDataDialogComponent, IPublishCollabResult | string>,
    private readonly fb: NonNullableFormBuilder,
    private readonly commonDataService: CommonDataService,
    private readonly collaborationOperationsService: CollaborationOperationsService,
    private readonly collaborationApiService: CollaborationApiService,
    private readonly collabDialogDataService: CollabDialogDataService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly notificationMessageService: NotificationMessageService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: IPublishCollabDialogData | null
  ) { }

  ngOnInit(): void {
    this.initializedForm();

  }

  private initializedForm(): void {
    this.publishCollabFormGroup = this.fb.group({
      modificationNotes: this.fb.control('', Validators.required),
      sendEmail: this.fb.control(false)
    });
  }

  async publish(): Promise<void> {
    if (this.publishCollabFormGroup.invalid) {
      return;
    }

    if (this.data) {
      const request: PublishRequest = {
        isModified: this.commonDataService.isSaveEnabled,
        modificationNotes: this.publishCollabFormGroup.controls.modificationNotes.value,
        sendEmail: this.publishCollabFormGroup.controls.sendEmail.value,
        estimateSaveRequest: this.data.estimateSaveRequest
      };
      let result = false;
      try {
        if (this.data.isAcceptAccessRequestPublish) {
          const res = await firstValueFrom(this.collaborationApiService.acceptAccessRequestAndPublish(request));
          // If the API call succeeded without throwing, it is successful (res can be true, null, or undefined for 200/204)
          result = res !== false;
          if (result) {
            this.commonDataService.setCollaborationState(CollaborationState.Open);
            this.collabDialogDataService.setPublished(true);
          }
        } else {
          result = await this.collaborationOperationsService.performPublish(request);
        }
      } catch (error) {
        if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
          return;
        }

        this.notificationMessageService.showError('ERRORS.GENERIC');
        return;
      }

      if (!result) {
        if (this.data.closeOnFailure) {
          this.dialogRef.close(CONSTANTS.PUBLISH_FAILED);
        }
        return;
      }
      const publishResult: IPublishCollabResult = {
        modificationNotes: this.publishCollabFormGroup.controls.modificationNotes.value
      };
      this.dialogRef.close(publishResult);
      return;
    }

    this.dialogRef.close({ modificationNotes: CONSTANTS.PUBLISHED });
  }
}
